// Social Lite music picker — mirrors the Social Video tool: search + vibe chips over the R2 library, with the
// Cyanite beat-mapped tracks highlighted in gold. With AI Beat Map ON only the beat-mapped tracks are offered.
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Search, Play, Pause, Music2, Check, Loader2, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_VIBES = ["upbeat", "chill", "cinematic", "luxury", "emotional", "modern", "general"];
const fmt = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "");

export function SocialLiteMusic({ value, onPick, beatMapOnly }) {
  const [q, setQ] = useState("");
  const [vibe, setVibe] = useState(null);
  const [vibes, setVibes] = useState([]);
  const [results, setResults] = useState([]);
  const [beatTracks, setBeatTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);

  useEffect(() => {
    axios.get(`${API}/music/beat-mapped`).then((r) => setBeatTracks((r.data.tracks || []).filter((t) => t.url))).catch(() => {});
    return () => { if (audio.current) { audio.current.pause(); audio.current = null; } };
  }, []);

  useEffect(() => {
    if (beatMapOnly) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/music/search`, { params: { query: q, vibe: vibe || undefined, page_size: 100 } });
        setResults(r.data.tracks || []);
        if (r.data.vibes?.length) setVibes(r.data.vibes);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, vibe, beatMapOnly]);

  const beatByUrl = useMemo(() => Object.fromEntries(beatTracks.map((t) => [t.url, t])), [beatTracks]);

  const preview = (track) => {
    if (playing === track.url) { audio.current?.pause(); setPlaying(null); return; }
    if (audio.current) audio.current.pause();
    audio.current = new Audio(track.url);
    audio.current.onended = () => setPlaying(null);
    audio.current.play().then(() => setPlaying(track.url)).catch(() => setPlaying(null));
  };
  const pick = (track) => {
    audio.current?.pause(); setPlaying(null);
    const beat = beatByUrl[track.url] || (track.bpm ? track : null);
    onPick({ url: track.url, name: beat?.name || track.name, bpm: beat?.bpm || null });
  };

  const Row = ({ track, beat }) => {
    const selected = value?.url === track.url;
    return (
      <div className={`slt-track ${beat ? "is-beat" : ""} ${selected ? "is-selected" : ""}`} data-testid={`social-lite-track-${track.id}`}>
        <button type="button" className={`slt-track-play ${playing === track.url ? "is-playing" : ""}`} onClick={() => preview(track)} aria-label="Preview" data-testid={`social-lite-track-play-${track.id}`}>
          {playing === track.url ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <div className="slt-track-info">
          <strong>{track.title || track.name}</strong>
          <small>
            {track.artist && <span className="slt-track-artist">{track.artist}</span>}
            {track.duration > 0 && <span>{fmt(track.duration)}</span>}
            {track.vibe && <span className="slt-track-vibe">{track.vibe}</span>}
            {beat && <span className="slt-track-ai" data-testid={`social-lite-track-ai-${track.id}`}><Zap size={10} /> AI · {beat.bpm} BPM</span>}
            {beat && (beat.mood || []).slice(0, 2).map((m) => <span key={m} className="slt-track-mood">{m}</span>)}
          </small>
          {beat && typeof beat.energy === "number" && <span className="slt-track-energy"><span style={{ width: `${Math.round(beat.energy * 100)}%` }} /></span>}
        </div>
        <button type="button" className={`slt-track-pick ${selected ? "is-selected" : ""}`} onClick={() => pick(track)} title={selected ? "Selected" : "Use this track"} data-testid={`social-lite-track-pick-${track.id}`}>
          {selected ? <Check size={14} /> : <Music2 size={14} />}
        </button>
      </div>
    );
  };

  if (beatMapOnly) {
    return (
      <div className="slt-music" data-testid="social-lite-music-beat">
        <p className="slt-music-hint"><Zap size={12} /> AI Beat Map is on — only tracks with beat analysis are offered, so every cut lands on the music.</p>
        {beatTracks.length === 0 ? <div className="slt-music-loading"><Loader2 size={14} className="slt-spin" /> Loading beat-mapped tracks…</div>
          : <div className="slt-track-list">{beatTracks.map((t) => <Row key={t.id} track={t} beat={t} />)}</div>}
      </div>
    );
  }
  return (
    <div className="slt-music" data-testid="social-lite-music-library">
      <div className="slt-music-search"><Search size={14} /><input placeholder="Search tracks…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="social-lite-music-search" /></div>
      <div className="slt-music-vibes">
        <span>Vibes:</span>
        {(vibes.length ? vibes : FALLBACK_VIBES).map((v) => <button key={v} type="button" className={`slt-vibe ${vibe === v ? "is-on" : ""}`} onClick={() => setVibe(vibe === v ? null : v)} data-testid={`social-lite-vibe-${v}`}>{v}</button>)}
        <button type="button" className={`slt-vibe ${!vibe ? "is-on" : ""}`} onClick={() => setVibe(null)} data-testid="social-lite-vibe-all">all</button>
        <span className="slt-music-legend"><Zap size={10} /> gold = beat-mapped (works with AI Beat Map)</span>
      </div>
      {loading && results.length === 0 ? <div className="slt-music-loading"><Loader2 size={14} className="slt-spin" /> Loading tracks…</div>
        : <div className="slt-track-list">{results.map((t) => <Row key={t.id} track={t} beat={beatByUrl[t.url]} />)}{results.length === 0 && !loading && <div className="slt-music-empty">No tracks match.</div>}</div>}
    </div>
  );
}
